import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "src/environments/environment";

/**
 * Optional “my calendar” conflict check while booking consultations.
 *
 * Backend contract (you implement on the API):
 * - GET  {api}api/UserCalendar/oauth/google/start?returnUrl=...
 * - GET  {api}api/UserCalendar/oauth/microsoft/start?returnUrl=...
 *   → Redirect user through OAuth; on success redirect back to returnUrl (e.g. with ?calendar_connected=1&calendar_provider=google).
 * - POST {api}api/UserCalendar/freebusy
 *   Body: { provider: "google" | "microsoft", timeMin: ISO string, timeMax: ISO string }
 *   Response: { busy: boolean }
 *
 * Until the API exists, free/busy calls fail silently (skipped: true).
 */
export type UserCalendarProviderChoice = "none" | "google" | "microsoft";

export interface UserCalendarFreeBusyResult {
  busy: boolean;
  skipped?: boolean;
}

export interface UserCalendarOAuthConfigResult {
  googleConfigured: boolean;
  microsoftConfigured: boolean;
}

@Injectable({
  providedIn: "root",
})
export class UserCalendarConflictService {
  private readonly storageKey = "flowchest_user_calendar_v1";

  constructor(private http: HttpClient) {}

  getConnection(): { provider: "google" | "microsoft" } | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return null;
      const o = JSON.parse(raw);
      if (o?.provider === "google" || o?.provider === "microsoft") {
        return { provider: o.provider };
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  isConnected(provider: Exclude<UserCalendarProviderChoice, "none">): boolean {
    const c = this.getConnection();
    return !!c && c.provider === provider;
  }

  markConnected(provider: "google" | "microsoft"): void {
    localStorage.setItem(
      this.storageKey,
      JSON.stringify({ provider, at: Date.now() })
    );
  }

  disconnect(): void {
    localStorage.removeItem(this.storageKey);
  }

  getOAuthStartUrl(
    provider: Exclude<UserCalendarProviderChoice, "none">,
    returnUrl: string,
    accessToken?: string | null
  ): string {
    const cfg = environment.userCalendar;
    const path =
      provider === "google"
        ? cfg.oauthGooglePath
        : cfg.oauthMicrosoftPath;
    const enc = encodeURIComponent(returnUrl);
    let url = `${environment.apiBaseUrl}${path}?returnUrl=${enc}`;
    if (accessToken) {
      url += `&access_token=${encodeURIComponent(accessToken)}`;
    }
    return url;
  }

  getOAuthConfig(): Observable<UserCalendarOAuthConfigResult> {
    const cfg = environment.userCalendar;
    const url = `${environment.apiBaseUrl}api/UserCalendar/oauth/config`;
    if (!cfg?.enabled) {
      return of({ googleConfigured: false, microsoftConfigured: false });
    }
    return this.http.get<UserCalendarOAuthConfigResult>(url).pipe(
      catchError(() => of({ googleConfigured: true, microsoftConfigured: true }))
    );
  }

  checkFreeBusy(
    provider: Exclude<UserCalendarProviderChoice, "none">,
    timeMin: string,
    timeMax: string
  ): Observable<UserCalendarFreeBusyResult> {
    const cfg = environment.userCalendar;
    if (!cfg?.enabled) {
      return of({ busy: false, skipped: true });
    }
    const url = `${environment.apiBaseUrl}${cfg.freeBusyPath}`;
    return this.http
      .post<{ busy?: boolean; Busy?: boolean }>(url, {
        provider,
        timeMin,
        timeMax,
      })
      .pipe(
        map((res) => ({
          busy: !!(res?.busy ?? res?.Busy),
        })),
        catchError(() => of({ busy: false, skipped: true }))
      );
  }
}
